import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
// import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
// import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";

const getAllStores = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  // return (user?.stores || []).filter((s: any) => s.status === 1);
  return user?.stores || [];
};

const getCurrentStoreId = () => localStorage.getItem("currentStoreId");

const setCurrentStoreId = (id: string) => localStorage.setItem("currentStoreId", String(id));

// ── Sub-component: StoreDropdown ──────────────────────────────
const StoreDropdown = ({ onStoreChange }: { onStoreChange?: (store: any) => void }) => {

const navigate = useNavigate();
const [stores, setStores] = useState<any[]>(getAllStores);

useEffect(() => {
  const sync = () => {
    const all = getAllStores();
    const savedId = getCurrentStoreId();
    setStores(all);
    const fresh = all.find((s: any) => String(s.id) === savedId) ?? all[0] ?? null;
    if (fresh) setSelectedStore(fresh);
  };
  window.addEventListener("user-updated", sync);
  window.addEventListener("store-changed", sync);
  return () => {
    window.removeEventListener("user-updated", sync);
    window.removeEventListener("store-changed", sync);
  };
}, []);


  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [selectedStore, setSelectedStore] = useState<any>(() => {
    const all = getAllStores();
    const savedId = getCurrentStoreId();
    const found = all.find((s: any) => String(s.id) === savedId);
    if (found) return found;
    if (all[0]) { setCurrentStoreId(String(all[0].id)); return all[0]; }
    return null;
    });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

const handleSelect = (store: any) => {
  setSelectedStore(store);
  setCurrentStoreId(store.id);
  setOpen(false);
  onStoreChange?.(store);
  window.dispatchEvent(new Event("store-changed"));

  const currentPath = window.location.pathname;
  if (currentPath.startsWith("/edit-store/") && !currentPath.startsWith("/edit-store-owner/")) {
    navigate(`/edit-store/${store.id}`, {
      replace: true,
      state: {
        storeName: store.name,
        studio_org_name: store.studio_organization_name,
        store_description: store.description,
      },
    });
  }
};

  const editStoreLink = selectedStore
  ? {
      to: `/edit-store/${selectedStore.id}`,
      state: {
        storeName: selectedStore.name,
        studio_org_name: selectedStore.studio_organization_name,
        store_description: selectedStore.description,
      },
    }
  : null;

  const isSelectedInactive = selectedStore?.status !== 1;

  const storeName = selectedStore?.name
      ?.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
      ?? "Select Store";

  const storeDisplayName = isSelectedInactive ? `${storeName} (Inactive)` : storeName;

  return (
    <div className="store-selector-wrap" ref={dropdownRef}>
      <div className="store-selector-bar">
        <StorefrontOutlinedIcon className="image-19" />
        <span className="store-name-tooltip-wrap">
          <h1 className={`store-name${isSelectedInactive ? " store-name--inactive" : ""}`}>{storeDisplayName}</h1>
          <span className="store-name-tooltip">{storeDisplayName}</span>
        </span>
        <div className="store-selector-actions">
          {/* 
          <button
            className="store-selector-list-btn"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          >
            <FormatListBulletedIcon fontSize="small" />
          </button>

          <span className="sidebar-tooltip-wrap" data-tooltip="Add Store">
            <IconButton size="small" className="store-selector-list-btn" onClick={() => navigate("/add-store/")}>
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </span>
          */}
          {editStoreLink && (
            <span className="sidebar-tooltip-wrap" data-tooltip="Edit Store">
              <Link to={editStoreLink.to} state={editStoreLink.state} className="store-action-link">
                <IconButton size="small" className="store-selector-list-btn">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Link>
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="store-selector-dropdown">
          <div className="store-selector-scroll">
            {stores.map((store: any) => {
              const name = store.name
                ?.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
              const isActive = String(store.id) === String(selectedStore?.id);
              const isInactive = store.status !== 1;
              return (
                <div
                  key={store.id}
                  className={`store-selector-item ${isActive ? "store-selector-item--active" : ""} ${isInactive ? "store-selector-item--disabled" : ""}`}
                  onClick={() => !isInactive && handleSelect(store)}
                  title={isInactive ? "Inactive store" : undefined}
                >
                  <span className="store-selector-item-name-wrap">
                    <span className="store-selector-item-name">{name}</span>
                    <span className="store-item-tooltip">{isInactive ? "Inactive store" : name}</span>
                  </span>
                  {isActive && <CheckIcon fontSize="small" className="store-selector-check" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Master component ──────────────────────────────────────────
const StoreSelector = ({ onStoreChange }: { onStoreChange?: (store: any) => void }) => {
  return <StoreDropdown onStoreChange={onStoreChange} />;
};

export default StoreSelector;
export { StoreDropdown, getAllStores, getCurrentStoreId };